<template>
  <div class="scs-IntegrationsPage">
    <AppBackButton />

    <PageContentHeader
      :level="3"
      :max-width="HEADER_SIZES.LG"
      class="scs-IntegrationsPage_Header"
      no-margin
      title="SSO"
    >
      <AppButton
        v-if="!isEmpty(ssoConnections)"
        v-tippy="{
          content: isSSOLimitExceeded
            ? $t('integration_settings.you_have_reached_connections_limit')
            : null
        }"
        :disable="isSSOLimitExceeded"
        height="24"
        icon="plus-next"
        type="primary-next"
        width="24"
        @click="onCreateSSOConnectionClick"
      >
        {{ $t('action.create_connection') }}
      </AppButton>
    </PageContentHeader>
    <div class="scs-MainContent">
      <AppTable
        v-if="!isNoSSOConnections"
        :columns="TABLE_COLUMNS"
        :data="ssoConnections"
        :disable-user-select="false"
        :hover-row="tableHoverRow"
        :loading="areSSOConnectionsLoading"
        border-on-last-row
        no-border
        offset-left="var(--page-left-padding)"
        offset-right="var(--page-right-padding)"
        sticky-header
        type="primary-next"
      >
        <template #header-cell="{ column }">
          <template v-if="column.title">
            {{ t(column.title) }}
          </template>
        </template>
        <template #cell="{ columnKey, item, index }">
          <template v-if="columnKey === TABLE_COLUMNS_KEYS.NUMBER">
            <div class="scs-SSONumber">
              {{ index + 1 }}
            </div>
          </template>
          <template v-if="columnKey === TABLE_COLUMNS_KEYS.NAME">
            <div class="scs-SSONameWrapper">
              <div class="oboard-truncated-text">{{ item[NAME] }}</div>
              <InlineRename
                :entity-name="item[NAME]"
                :is-loading="renameLoading"
                :max-length="SSO_NAME_MAX_LENGTH"
                dropdown-width="438px"
                @update-name="onUpdateName({ ...$event, [ID]: item[ID] })"
              />
            </div>
          </template>
          <template v-if="columnKey === TABLE_COLUMNS_KEYS.PROVIDER">
            <div class="scs-StringCell oboard-truncated-text">
              {{ getProviderName(item[PROVIDER]) }}
            </div>
          </template>
          <template v-if="columnKey === TABLE_COLUMNS_KEYS.PROTOCOL">
            <div class="scs-StringCell oboard-truncated-text">
              {{ CONNECTIONS[item[PROTOCOL]] }}
            </div>
          </template>

          <template v-if="columnKey === TABLE_COLUMNS_KEYS.DOMAINS && isSaml(item[PROTOCOL])">
            <LimitTagList
              :entity="LIMIT_TAG_LIST_ENTITIES.DOMAIN"
              :items="item[DOMAINS] || []"
              :limit="3"
              style="--list-gap: 4px; --count-offset: 0"
              :item-value="SAML_DOMAIN_ENTITY_KEYS.DOMAIN_NAME"
              :tooltip-value="SAML_DOMAIN_ENTITY_KEYS.DOMAIN_NAME"
            />
          </template>

          <template v-if="columnKey === TABLE_COLUMNS_KEYS.STATUS && isSaml(item[PROTOCOL])">
            <ObjectiveStatus :status="getStatusCellParams(item).status">
              {{ getStatusCellParams(item).content }}
            </ObjectiveStatus>
          </template>

          <template v-if="columnKey === TABLE_COLUMNS_KEYS.CREATED">
            <div class="scs-StringCell oboard-truncated-text">
              {{ formatDateForAuditLog(item[CREATED_DATE]) }}
            </div>
          </template>

          <template v-else-if="columnKey === TABLE_COLUMNS_KEYS.ACTION">
            <div class="scs-Actions">
              <DropdownMenu
                :items="getMenuActions(item)"
                :offset="[0, 0]"
                :type="DROPDOWN_MENU_TYPES.DEFAULT_NEXT"
                preferred-position="bottom-end"
                @close="tableHoverRow = null"
                @open="tableHoverRow = index"
                @item-click="onMenuActionsClick($event, item)"
              >
                <template #activator>
                  <AppButton icon="more-next" size="sm" type="tertiary-next" />
                </template>
              </DropdownMenu>
            </div>
          </template>
        </template>
        <template #loading>
          <SSOLoader />
        </template>

        <template #footer>
          <AppTableCreateButton
            v-if="!createButtonDisabled"
            full-width
            icon-name="plus-next"
            @click="onCreateSSOConnectionClick"
          >
            {{ t('action.create_connection') }}
          </AppTableCreateButton>

          <AppInfoMessage
            v-if="isSSOLimitExceeded"
            :type="INFO_MESSAGE_TYPES.WARNING"
            class="scs-Message"
          >
            {{ t('integration_settings.you_have_reached_connections_limit') }}
          </AppInfoMessage>
        </template>
      </AppTable>
    </div>

    <SettingsPagesEmptyState
      v-if="isNoSSOConnections"
      hero-height="128"
      hero-icon="no-tokens-hero"
      hero-width="142"
    >
      <template #title>
        {{ t('integration_settings.no_sso') }}
      </template>
      <template #subtitle> {{ t('integration_settings.create_first_sso') }}</template>

      <template #action>
        <AppButton
          height="24"
          icon="plus-next"
          type="primary-next"
          width="24"
          @click="onCreateSSOConnectionClick"
        >
          {{ t('integration_settings.create_sso') }}
        </AppButton>
      </template>
    </SettingsPagesEmptyState>
    <portal to="modal-windows">
      <CreateSsoConnectionModal
        v-model:show="showConnectionModal"
        :exceeded-limits="exceededLimits"
        @created="getSSO"
        @setup-certificate="setupCertificate"
      />

      <DeleteSsoConnectionModal
        v-model:show="showDeleteSSOModal"
        :protocol-to-delete="protocolToDelete"
        @deleted="getSSO"
      />

      <CertificateModal
        v-model:show="showCertificateModal"
        :connection="connectionForSetupCertificate"
        @reload-connections="getSSO"
        @setup-activation="runSetupActivation"
      />

      <ActivateConnectionModal
        v-model:show="showActivateConnectionModal"
        :connection="connectionForActivation"
        @reload-connections="getSSO"
      />
    </portal>
  </div>
</template>

<script setup>
import { isEmpty, isFunction } from 'lodash'
import { computed, nextTick, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'

import SSOApiHandler from '@/api/sso'
import { ROUTE_NAMES } from '@/routes/route-helpers'
import { ACTIONS_KEYS } from '@/utils/actions-keys'
import { formatDateForAuditLog } from '@/utils/audit-logs'
import { INFO_MESSAGE_TYPES } from '@/utils/components-configurations/app-info-message'
import { DROPDOWN_MENU_TYPES } from '@/utils/components-configurations/dropdown-menu'
import { LIMIT_TAG_LIST_ENTITIES } from '@/utils/components-configurations/limit-tag-list'
import { HEADER_SIZES } from '@/utils/components-configurations/page-content-header'
import { SAML_CONNECTION_ENTITY_KEYS, SAML_DOMAIN_ENTITY_KEYS } from '@/utils/entity-keys'
import { handleError } from '@/utils/error-handling'
import {
  CONNECTIONS,
  CONNECTIONS_PROTOCOLS,
  isSCIM,
  getProviders,
  SSO_NAME_MAX_LENGTH,
  isSaml,
  SAML_CONNECTION_STATUS_IDS,
  isSamlConnectionActive
} from '@/utils/sso-connection-settings'
import { TABLE_COLUMNS_KEYS } from '@/utils/table-columns'
import { usePlansLimitations } from '@/utils/web-app/plans-limitations'

import LimitTagList from '@/components/form/LimitTagList'
import ActivateConnectionModal from '@/components/integrations/sso/ActivateConnectionModal'
import CertificateModal from '@/components/integrations/sso/CertificateModal'
import CreateSsoConnectionModal from '@/components/integrations/sso/CreateSSOConnectionModal'
import DeleteSsoConnectionModal from '@/components/integrations/sso/DeleteSSOConnectionModal'
import ObjectiveStatus from '@/components/objectives/ObjectiveStatus'
import AppBackButton from '@/components/ui/AppBackButton'
import AppButton from '@/components/ui/AppButton/AppButton'
import AppInfoMessage from '@/components/ui/AppInfoMessage'
import AppTableCreateButton from '@/components/ui/AppTableCreateButton'
import DropdownMenu from '@/components/ui/DropdownMenu/DropdownMenu'
import InlineRename from '@/components/ui/InlineRename/InlineRename'
import PageContentHeader from '@/components/ui/PageContentHeader/PageContentHeader'
import SettingsPagesEmptyState from '@/components/ui/SettingsPagesEmptyState/SettingsPagesEmptyState'
import SSOLoader from '@/components/ui/SkeletonLoaders/SSOLoader'
import AppTable from '@/components/ui/Table/AppTable'

defineOptions({
  name: 'SSOConnectionSettings'
})

const { NAME, PROVIDER, PROTOCOL, DOMAINS, ID, CREATED_DATE } = SAML_CONNECTION_ENTITY_KEYS

const { t } = useI18n()

const TABLE_COLUMNS = [
  {
    title: 'tokens.token_number',
    key: TABLE_COLUMNS_KEYS.NUMBER,
    width: 24
  },
  {
    title: 'users.table_header_name',
    key: TABLE_COLUMNS_KEYS.NAME,
    width: 'auto'
  },
  {
    title: 'integration_settings.table_header_provider',
    key: TABLE_COLUMNS_KEYS.PROVIDER,
    width: 100
  },
  {
    title: 'integration_settings.table_header_protocol',
    key: TABLE_COLUMNS_KEYS.PROTOCOL,
    width: 100
  },
  {
    title: 'integration_settings.table_header_domains',
    key: TABLE_COLUMNS_KEYS.DOMAINS,
    width: 340
  },
  {
    title: 'field.status.title',
    key: TABLE_COLUMNS_KEYS.STATUS,
    width: 119
  },
  {
    title: 'integration_settings.table_header_created',
    key: TABLE_COLUMNS_KEYS.CREATED,
    width: 200
  },
  {
    key: TABLE_COLUMNS_KEYS.ACTION,
    width: 24
  }
]

const tableHoverRow = ref(null)

const getMenuActions = item => {
  const actions = [
    {
      name: ACTIONS_KEYS.REMOVE,
      title: 'action.delete',
      icon: 'delete-next',
      color: 'var(--grade-low-color-next)'
    }
  ]

  if (isSaml(item[PROTOCOL])) {
    const isActive = isSamlConnectionActive(item)

    if (isActive) {
      return actions
    }

    const isNoCertificate =
      item[SAML_CONNECTION_ENTITY_KEYS.STATUS_ID] ===
      SAML_CONNECTION_STATUS_IDS.INACTIVE_NO_CERTIFICATE

    const action = {
      name: isNoCertificate ? ACTIONS_KEYS.ADD_CERTIFICATE : ACTIONS_KEYS.ACTIVATE,
      title: 'action.activate',
      icon: 'round-checkbox-checked'
    }

    actions.unshift(action)
  }

  return actions
}

const onMenuActionsClick = (actionName, connection) => {
  if (actionName === ACTIONS_KEYS.REMOVE) {
    onDeleteSSO(connection)
  } else if (actionName === ACTIONS_KEYS.ADD_CERTIFICATE) {
    setupCertificate(connection)
  } else if (actionName === ACTIONS_KEYS.ACTIVATE) {
    setupActivation(connection)
  }
}

const getProviderName = item => {
  return getProviders().find(({ value }) => value === item)?.label || 'unknown'
}

const ssoConnections = ref([])

const protocolToDelete = ref({})
const showDeleteSSOModal = ref(false)
const areSSOConnectionsLoading = ref(false)

const isNoSSOConnections = computed(() => {
  return isEmpty(ssoConnections.value) && !areSSOConnectionsLoading.value
})

const MAX_SSO_CONNECTION_COUNT = 10

const exceededLimits = computed(() => {
  const countsByProtocol = {}
  const exceeded = []

  for (const { protocol } of ssoConnections.value) {
    const total = (countsByProtocol[protocol] = (countsByProtocol[protocol] || 0) + 1)
    if (total >= MAX_SSO_CONNECTION_COUNT) {
      exceeded.push(protocol)
    }
  }

  return exceeded
})

const isSSOLimitExceeded = computed(() => {
  return exceededLimits.value.length === CONNECTIONS_PROTOCOLS.length
})

const createButtonDisabled = computed(() => {
  return areSSOConnectionsLoading.value || isSSOLimitExceeded.value
})

const onCreateSSOConnectionClick = () => {
  showConnectionModal.value = true
}

const showConnectionModal = ref(false)

const getSSO = async () => {
  const api = new SSOApiHandler()

  areSSOConnectionsLoading.value = true
  try {
    ssoConnections.value = await api.getSSO()
  } catch (error) {
    handleError({ error })
  } finally {
    areSSOConnectionsLoading.value = false
  }
}

const router = useRouter()
const { SSOLimitations } = usePlansLimitations()

onMounted(() => {
  if (!SSOLimitations.value.isAvailable) {
    router.push({ name: ROUTE_NAMES.MISSING_PAGE })
    return
  }

  getSSO()
})

const onDeleteSSO = async item => {
  protocolToDelete.value = item
  showDeleteSSOModal.value = true
}

const renameLoading = ref(false)

const onUpdateName = async ({ newName, callback, id }) => {
  renameLoading.value = true
  try {
    const api = new SSOApiHandler()
    const protocol = ssoConnections.value.find(s => s.id === id)?.protocol
    if (isSCIM(protocol)) {
      await api.updateScimConnectionName({ id, name: newName })
    } else {
      await api.updateSamlConnectionName({ id, name: newName })
    }
    await getSSO()

    renameLoading.value = false
    await nextTick()

    if (callback && isFunction(callback)) {
      callback()
    }
  } catch (error) {
    handleError({ error })
  } finally {
    renameLoading.value = false
  }
}

const connectionForSetupCertificate = ref(null)
const showCertificateModal = ref(false)

const setupCertificate = connection => {
  connectionForSetupCertificate.value = connection
  showCertificateModal.value = true
}

const showActivateConnectionModal = ref(false)
const connectionForActivation = ref(null)

const setupActivation = connection => {
  connectionForActivation.value = connection
  showActivateConnectionModal.value = true
}

const runSetupActivation = async connectionId => {
  await getSSO()

  if (connectionId) {
    const _connection = ssoConnections.value.find(c => c[ID] === connectionId)

    if (_connection) {
      setupActivation(_connection)
    }
  }
}

const getStatusCellParams = (connection = {}) => {
  const isActive = isSamlConnectionActive(connection)

  return {
    status: Number(isActive),
    content: isActive ? t('filter.active') : t('filter.inactive')
  }
}
</script>
<style lang="scss" scoped>
@import '~@/assets/styles/app-table-helpers';

.scs-IntegrationsPage_Header {
  &:deep(.pc-Title) {
    min-height: 32px;
  }
}

.scs-IntegrationsPage {
  width: 100%;
  max-width: $page-width-lg;
  padding-bottom: $page-bottom-padding;
}

.scs-MainContent {
  width: 100%;
  max-width: 100%;
  padding-left: $page-left-padding;
  padding-right: $page-right-padding;
}

.scs-Actions {
  @extend %app-table-hidden-items;
}

.scs-Message {
  margin-top: 10px;
  padding-left: $page-left-padding;
}

.scs-SSONumber {
  font-weight: fw('bold');
  color: $dark-3;
}

.scs-StringCell {
  font-weight: fw('semi-bold');
  padding-right: 8px;
}

.scs-SSONameWrapper {
  display: flex;
  align-items: center;
  gap: 4px;
  padding-right: 8px;
}
</style>
